import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, from } from 'rxjs';
import { collection, query, orderBy, limit, onSnapshot, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { takeUntil } from 'rxjs/operators';

export interface OrderNotification {
  id: string;
  orderId: string;
  orderDate: Date;
  productNames: string[];
  totalAmount: number;
  isRead: boolean;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class AdminNotificationService implements OnDestroy {
  private notificationsSubject = new BehaviorSubject<OrderNotification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();
  
  private readonly ORDERS_COLLECTION = 'orderTable';
  private readonly STORAGE_KEY = 'admin_notifications';
  private unsubscribe$ = new Subject<void>();
  private unsubscribeSnapshot: (() => void) | null = null;
  private lastOrderTimestamp: Date | null = null;

  constructor() {
    this.loadStoredNotifications();
    this.startListeningForNewOrders();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
    }
  }

  /**
   * Load stored notifications from localStorage
   */
  private loadStoredNotifications(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const notifications: OrderNotification[] = JSON.parse(stored).map((n: any) => ({
          ...n,
          orderDate: new Date(n.orderDate),
          timestamp: new Date(n.timestamp)
        }));
        this.notificationsSubject.next(notifications);
        this.updateUnreadCount();
      }
    } catch (error) {
      console.error('Error loading stored notifications:', error);
    }
  }

  /**
   * Save notifications to localStorage
   */
  private saveNotifications(): void {
    try {
      const notifications = this.notificationsSubject.value;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }

  /**
   * Start listening for new orders in real-time
   */
  private startListeningForNewOrders(): void {
    // Get the most recent order date from stored notifications
    const existingNotifications = this.notificationsSubject.value;
    if (existingNotifications.length > 0) {
      // Get the latest notification timestamp
      const latestNotification = existingNotifications[0];
      this.lastOrderTimestamp = latestNotification.timestamp;
    } else {
      // If no stored notifications, set timestamp to now (only notify for future orders)
      this.lastOrderTimestamp = new Date();
    }

    const ordersRef = collection(db, this.ORDERS_COLLECTION);
    const q = query(
      ordersRef,
      orderBy('orderDate', 'desc'),
      limit(100) // Listen to last 100 orders to catch any missed ones
    );

    // Track if this is the first snapshot (initial load)
    let isFirstSnapshot = true;

    this.unsubscribeSnapshot = onSnapshot(
      q,
      (snapshot) => {
        // Skip processing on first snapshot to avoid notifying for existing orders
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          
          // Update lastOrderTimestamp to the most recent order to establish baseline
          // This ensures we only notify for orders that come after this point
          if (snapshot.docs.length > 0) {
            const latestOrder = snapshot.docs[0];
            const orderData = latestOrder.data();
            const orderDate = this.convertTimestamp(orderData['orderDate']);
            // Only update if we don't have a stored timestamp or if current timestamp is "now" (fresh init)
            const now = new Date();
            const timeDiff = Math.abs(this.lastOrderTimestamp!.getTime() - now.getTime());
            if (!this.lastOrderTimestamp || timeDiff < 5000) { // Within 5 seconds of now = fresh init
              this.lastOrderTimestamp = orderDate;
            } else if (orderDate > this.lastOrderTimestamp) {
              // If stored timestamp exists but this order is newer, update it
              this.lastOrderTimestamp = orderDate;
            }
          }
          return;
        }

        // Process only newly added documents (new orders)
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const orderData = change.doc.data();
            const orderDate = this.convertTimestamp(orderData['orderDate']);
            
            // Only process orders that are newer than our last known order
            if (!this.lastOrderTimestamp || orderDate > this.lastOrderTimestamp) {
              this.addNewOrderNotification(change.doc.id, orderData);
              this.lastOrderTimestamp = orderDate;
            }
          }
        });
      },
      (error) => {
        console.error('Error listening to orders:', error);
      }
    );
  }

  /**
   * Convert Firestore timestamp to Date
   */
  private convertTimestamp(timestamp: any): Date {
    if (timestamp?.toDate) {
      return timestamp.toDate();
    } else if (timestamp instanceof Date) {
      return timestamp;
    } else if (timestamp?.seconds) {
      return new Date(timestamp.seconds * 1000);
    } else {
      return new Date(timestamp);
    }
  }

  /**
   * Add a new order notification
   */
  private addNewOrderNotification(orderId: string, orderData: any): void {
    const productNames = (orderData['products'] || []).map((p: any) => {
      // Try to get product name from various possible fields
      return p.name || p.productName || p.title || p.id || 'Unknown Product';
    });

    const orderDate = this.convertTimestamp(orderData['orderDate']);
    
    const notification: OrderNotification = {
      id: `notif_${Date.now()}_${orderId}`,
      orderId: orderId,
      orderDate: orderDate,
      productNames: productNames.length > 0 ? productNames : ['Order'],
      totalAmount: orderData['totalAmount'] || 0,
      isRead: false,
      timestamp: new Date()
    };

    const currentNotifications = this.notificationsSubject.value;
    // Add to beginning of array (newest first)
    const updatedNotifications = [notification, ...currentNotifications].slice(0, 50); // Keep last 50
    this.notificationsSubject.next(updatedNotifications);
    this.saveNotifications();
    this.updateUnreadCount();
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notificationId: string): void {
    const notifications = this.notificationsSubject.value;
    const updated = notifications.map(n => 
      n.id === notificationId ? { ...n, isRead: true } : n
    );
    this.notificationsSubject.next(updated);
    this.saveNotifications();
    this.updateUnreadCount();
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    const notifications = this.notificationsSubject.value;
    const updated = notifications.map(n => ({ ...n, isRead: true }));
    this.notificationsSubject.next(updated);
    this.saveNotifications();
    this.updateUnreadCount();
  }

  /**
   * Delete a notification
   */
  deleteNotification(notificationId: string): void {
    const notifications = this.notificationsSubject.value;
    const updated = notifications.filter(n => n.id !== notificationId);
    this.notificationsSubject.next(updated);
    this.saveNotifications();
    this.updateUnreadCount();
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notificationsSubject.next([]);
    localStorage.removeItem(this.STORAGE_KEY);
    this.updateUnreadCount();
  }

  /**
   * Update unread count
   */
  private updateUnreadCount(): void {
    const notifications = this.notificationsSubject.value;
    const unreadCount = notifications.filter(n => !n.isRead).length;
    this.unreadCountSubject.next(unreadCount);
  }

  /**
   * Get current unread count
   */
  getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Get all notifications
   */
  getNotifications(): OrderNotification[] {
    return this.notificationsSubject.value;
  }
}

